/**
 * BackchannelPlan — ANTICIPATED backchannels for duplex v2's second channel. Interim text lands 300-700ms late, so a
 * reactive "should I say mm-hm now?" is decided after the moment passed. Instead, one fast-model call OFF the hot path
 * (after the agent's reply / at the user's turn start, revised once after a few words) predicts the user's next turn:
 * its type, how dense backchannels should be, and textual cues ("first", "and then", "unfortunately") each paired with a
 * fitting short listener line. Live, every interim is matched against the plan with cheap text checks only (matchCue).
 */
import { forComponent } from '../logging';
import type { ChatLike, ChatResponse } from '../llm';
import { cleanLine, languageName } from './channelLang';

const log = forComponent('BackchannelPlan');

export const TURN_TYPES = ['story', 'list', 'complaint', 'explanation', 'short_command', 'question', 'other'] as const;
export type TurnType = typeof TURN_TYPES[number];
export const DENSITIES = ['none', 'low', 'normal', 'high'] as const;
export type Density = typeof DENSITIES[number];

export interface Cue { when: string[]; line: string }
export interface BcPlan { turn: TurnType; density: Density; cues: Cue[] }

/** The plan's micro-context — never history. */
export interface PlanContext {
  /** The agent's last reply (the user's next turn answers it). */
  agent: string;
  /** The tail of the user's previous turn. */
  user: string;
  /** Recent channel lines — not to be reused. */
  recent: string[];
  lang: string;
  /** Revision only: what the user has said so far in this turn. */
  heard?: string;
}

/** Turn types where a backchannel is wrong whatever the cue (it wants the answer, or is over before a breath). */
export const silentTurn = (p: BcPlan): boolean => p.density === 'none' || p.turn === 'short_command' || p.turn === 'question';

/** Lowercased words without punctuation (unicode-aware — Hebrew/Cyrillic cues match too). */
export const cueWords = (s: string): string[] => s.toLowerCase().replace(/[^\p{L}\p{N}'\s]+/gu, ' ').split(/\s+/).filter(Boolean);

/** A model reply → a valid plan, or null (no JSON, unknown type, …). Cues without a usable phrase or line are dropped. */
export function parsePlan(raw: string, lang?: string, maxCues = 8): BcPlan | null {
  const m = raw.match(/\{[\s\S]*\}/);
  if (!m) return null;
  let o: any;
  try { o = JSON.parse(m[0]); } catch { return null; }
  const turn = String(o?.turn ?? '').toLowerCase().replace(/[\s-]+/g, '_') as TurnType;
  const density = String(o?.density ?? '').toLowerCase() as Density;
  if (!TURN_TYPES.includes(turn) || !DENSITIES.includes(density)) return null;
  const cues: Cue[] = [];
  for (const c of Array.isArray(o.cues) ? o.cues : []) {
    const when = (Array.isArray(c?.when) ? c.when : [c?.when]).filter((w: unknown) => typeof w === 'string')
      .map((w: string) => cueWords(w).join(' ')).filter((w: string) => w && w.split(' ').length <= 4);
    const line = typeof c?.line === 'string' ? cleanLine(c.line, lang, 4) : '';
    if (when.length && line) cues.push({ when: [...new Set<string>(when)], line });
    if (cues.length >= maxCues) break;
  }
  return { turn, density, cues };
}

/** The first cue whose phrase occurs in `heard` as whole words starting at word index ≥ `from` (words already matched
 *  or backchanneled are not matched again). Pure text — the hot path. */
export function matchCue(plan: BcPlan | null, heard: string, from = 0): Cue | null {
  if (!plan || silentTurn(plan)) return null;
  const w = cueWords(heard);
  for (let i = Math.max(0, from); i < w.length; i++) {
    for (const c of plan.cues) {
      for (const p of c.when) {
        const pw = p.split(' ');
        if (i + pw.length <= w.length && pw.every((x, j) => w[i + j] === x)) return c;
      }
    }
  }
  return null;
}

export class BackchannelPlannerOptions {
  /** Fast model (Groq gpt-oss / the reflex). */
  model = 'groq/openai/gpt-oss-120b';
  maxTokens = 900;
}

/** One fast-model call per plan / revision. Throws on transport errors; null on an unusable reply. */
export class BackchannelPlanner {
  public constructor(private ai: ChatLike, public options?: Partial<BackchannelPlannerOptions>) {
    this.options = { ...new BackchannelPlannerOptions(), ...options };
  }

  async plan(c: PlanContext, signal?: AbortSignal): Promise<BcPlan | null> {
    const o = this.options as BackchannelPlannerOptions;
    const name = languageName(c.lang);
    const prompt =
      `You plan a live voice assistant's LISTENER sounds ("mm-hm", "right", "oh no") for the user's ${c.heard ? 'CURRENT' : 'NEXT'} spoken turn.\n` +
      `Assistant just said: "${c.agent.slice(-400)}"\n` + (c.user ? `User's previous turn ended: "${c.user.slice(-200)}"\n` : '') +
      (c.heard ? `User so far in this turn: "${c.heard.slice(-300)}"\n` : '') +
      `Predict the turn: turn = story | list | complaint | explanation | short_command | question | other; ` +
      `density = none (short request or a question: never interrupt) | low | normal | high (a long story wants frequent cues).\n` +
      `cues: 3-8 short words/phrases the user is likely to say at a clause boundary where a listener cue fits ` +
      `(list markers like "first", "and then", "also"; narrative markers; emotional words; names/topics from the context), ` +
      `each with ONE fitting listener line of 1-3 words in ${name} that reacts to it (sympathy for bad news, "okay" for a list step) — never a question, never an answer.\n` +
      `Reply JSON only: {"turn":"...","density":"...","cues":[{"when":["phrase",...],"line":"..."}]}. Phrases in the user's language.`;
    const reasoning = /gpt-oss/.test(o.model) ? { providerOptions: { reasoning_effort: 'low' } } : {};
    const r = (await this.ai.chat({ model: o.model, messages: [{ role: 'user', content: prompt }], stream: false, signal, maxTokens: o.maxTokens, ...reasoning })) as ChatResponse;
    const p = parsePlan(r.content ?? '', c.lang);
    if (!p) log.debug(`unusable plan: ${(r.content ?? '').slice(0, 120)}`);
    return p;
  }
}
